import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, RefreshCw, Search, Filter, Download, TrendingUp, TrendingDown,
  Package, Truck, Calendar, AlertCircle, CheckCircle, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportPOvsDelivered() {
  const [poData, setPOData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  useEffect(() => {
    fetchPOData();
  }, []);

  const fetchPOData = async () => {
    setLoading(true);
    try {
      // Use the new optimized endpoint that properly calculates delivered quantities
      const response = await axios.get(`${API}/imports/po-vs-delivered`);
      const data = response.data || [];
      
      // Map the data to match the expected format
      const mappedData = data.map(po => ({
        ...po,
        totalOrdered: po.orderedQty || 0,
        totalDelivered: po.receivedQty || 0,
        pending: po.pendingQty || 0,
        deliveryPercent: po.percentComplete || 0,
        deliveryStatus: po.status === 'Complete' ? 'Completed' : 
                        po.status === 'Partial' ? 'Partial' : 'Pending',
        grnCount: po.grnsCount || 0,
        supplierName: po.supplier || '',
        poDate: po.orderDate || ''
      }));
      
      setPOData(mappedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setPOData([]);
    }
    setLoading(false);
  };

  const filteredData = poData.filter(po => {
    const matchesSearch = !searchTerm || 
      po.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.supplierName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || po.deliveryStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate summary stats
  const totalPOs = filteredData.length;
  const completedPOs = filteredData.filter(po => po.deliveryStatus === 'Completed').length;
  const partialPOs = filteredData.filter(po => po.deliveryStatus === 'Partial').length;
  const pendingPOs = filteredData.filter(po => po.deliveryStatus === 'Pending').length;
  const totalOrderedQty = filteredData.reduce((sum, po) => sum + po.totalOrdered, 0);
  const totalDeliveredQty = filteredData.reduce((sum, po) => sum + po.totalDelivered, 0);

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'bg-green-100 text-green-700',
      'Partial': 'bg-yellow-100 text-yellow-700',
      'Pending': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 50) return 'bg-yellow-500';
    if (percent > 0) return 'bg-orange-500';
    return 'bg-gray-300';
  };

  return (
    <div className="space-y-6" data-testid="import-po-vs-delivered-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
            <BarChart3 className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Import PO vs Delivered</h1>
            <p className="text-sm text-gray-500">Track purchase order deliveries and pending quantities</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchPOData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition shadow-md"
          >
            <Download size={18} />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <Package size={16} />
            Total POs
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalPOs}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
            <CheckCircle size={16} />
            Completed
          </div>
          <p className="text-2xl font-bold text-green-600">{completedPOs}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-yellow-600 text-sm mb-1">
            <Clock size={16} />
            Partial
          </div>
          <p className="text-2xl font-bold text-yellow-600">{partialPOs}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
            <AlertCircle size={16} />
            Pending
          </div>
          <p className="text-2xl font-bold text-red-600">{pendingPOs}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <TrendingUp size={16} />
            Ordered (MT)
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalOrderedQty.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-blue-600 text-sm mb-1">
            <Truck size={16} />
            Delivered (MT)
          </div>
          <p className="text-2xl font-bold text-blue-600">{totalDeliveredQty.toFixed(2)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by PO number or supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
        >
          <option value="all">All Status</option>
          <option value="Completed">Completed</option>
          <option value="Partial">Partial</option>
          <option value="Pending">Pending</option>
        </select>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PO Number</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PO Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Ordered (MT)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Delivered (MT)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Pending (MT)</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Progress</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">GRNs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    Loading data...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                    No purchase orders found.
                  </td>
                </tr>
              ) : (
                filteredData.map((po) => (
                  <tr key={po.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-blue-600">{po.poNumber}</td>
                    <td className="px-4 py-3 text-gray-700">{po.supplierName}</td>
                    <td className="px-4 py-3 text-gray-600">{po.poDate}</td>
                    <td className="px-4 py-3 text-right text-gray-700 font-medium">{po.totalOrdered.toFixed(3)}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium">{po.totalDelivered.toFixed(3)}</td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium">{po.pending.toFixed(3)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getProgressColor(po.deliveryPercent)} transition-all`}
                            style={{ width: `${Math.min(100, po.deliveryPercent)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-600 w-12 text-right">
                          {po.deliveryPercent}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(po.deliveryStatus)}`}>
                        {po.deliveryStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        {po.grnCount}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
